/**
 * Client-side JWT checks (no signature verification — server remains source of truth).
 * Used to avoid treating garbage/expired tokens in localStorage as a logged-in session.
 */

function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** JWT payloads must be JSON objects — not arrays (`typeof [] === "object"`) or primitives. */
function isJwtPayloadObject(payload) {
  return (
    payload != null &&
    typeof payload === "object" &&
    !Array.isArray(payload)
  );
}

/**
 * Clock skew tolerance (ms). Compare `exp` against `Date.now() - skew` so a client
 * slightly ahead of the issuer does not clear the session early; adding skew to
 * `now` would wrongly require ~5s of remaining lifetime.
 */
const EXP_SKEW_MS = 5000;

/**
 * @returns {boolean} true if token looks like a JWT and is not past `exp` (if present).
 */
export function isAccessTokenValid(token) {
  const payload = decodeJwtPayload(token);
  if (!isJwtPayloadObject(payload)) return false;
  if (payload.exp == null) return true;
  const expSec = Number(payload.exp);
  if (!Number.isFinite(expSec)) return false;
  return expSec * 1000 > Date.now() - EXP_SKEW_MS;
}

/** Clears typical session keys so stale id/token pairs do not persist. */
export function clearClientSession() {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem("token");
    localStorage.removeItem("userID");
    localStorage.removeItem("userId");
  } catch {
    // ignore
  }
}
